import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaMapMarkerAlt, FaTimes } from "react-icons/fa";
import { toast } from "sonner";
import PublicMobileHeader from "./PublicMobileHeader";

const Maps_API_KEY = "AIzaSyCRQXge0eI79YVPiihiUrqOKv4NapMnA9I"; // Replace with your actual API Key

const PublicHeader = () => {
  const navigate = useNavigate();
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [currentLocation, setCurrentLocation] = useState({
    city: "",
    state: "",
    latitude: null,
    longitude: null,
    country: "",
    area: "",
  });
  const [manualLocationInput, setManualLocationInput] = useState("");
  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const locationInputModalRef = useRef(null);
  const autocompleteInstance = useRef(null);

  const initMap = useCallback(() => {
    setIsGoogleMapsLoaded(true);
  }, []);

  useEffect(() => {
    if (window.google && window.google.maps && window.google.maps.places) {
      setIsGoogleMapsLoaded(true);
      return;
    }

    window.initMap = initMap;

    const existingScript = document.querySelector(
      `script[src*="maps.googleapis.com/maps/api/js"]`
    );

    if (existingScript) {
      existingScript.onload = () => setIsGoogleMapsLoaded(true);
      existingScript.onerror = () =>
        toast.error("Failed to load Google Maps API.");
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${Maps_API_KEY}&libraries=places&callback=initMap`;
    script.async = true;
    script.defer = true;
    script.id = "google-maps-script-header";

    script.onerror = () => {
      toast.error("Failed to load Google Maps API for location services.");
    };

    document.head.appendChild(script);

    return () => {
      if (window.initMap === initMap) {
        delete window.initMap;
      }
    };
  }, [initMap]);

  useEffect(() => {
    if (
      isGoogleMapsLoaded &&
      showLocationModal &&
      locationInputModalRef.current
    ) {
      if (!autocompleteInstance.current) {
        try {
          autocompleteInstance.current =
            new window.google.maps.places.Autocomplete(
              locationInputModalRef.current,
              { types: ["geocode", "establishment"] }
            );

          autocompleteInstance.current.addListener("place_changed", () => {
            const place = autocompleteInstance.current.getPlace();

            if (place.geometry && place.formatted_address) {
              let city = "Unknown City";
              let state = "";
              let latitude = place.geometry.location.lat();
              let longitude = place.geometry.location.lng();
              let country = "";
              let area = "";

              const addressComponents = place.address_components;
              addressComponents.forEach((component) => {
                if (component.types.includes("locality")) {
                  city = component.long_name;
                } else if (
                  component.types.includes("administrative_area_level_1")
                ) {
                  state = component.long_name;
                } else if (component.types.includes("country")) {
                  country = component.long_name;
                }

                // Enhanced area detection
                if (!area) {
                  const areaTypes = [
                    "neighborhood",
                    "sublocality_level_1",
                    "sublocality",
                    "political",
                    "route",
                  ];

                  for (const type of areaTypes) {
                    if (component.types.includes(type)) {
                      area = component.long_name;
                      break;
                    }
                  }
                }
              });

              // Fallback for area
              if (!area && place.formatted_address) {
                area = place.formatted_address.split(",")[0].trim();
              }

              const newLocation = {
                city,
                state,
                latitude,
                longitude,
                country,
                area,
              };
              setCurrentLocation(newLocation);
              localStorage.setItem("userLocation", JSON.stringify(newLocation));
              setManualLocationInput(place.formatted_address);
              setShowLocationModal(false);
              toast.success("Location updated!");
            } else {
              toast.warn("No details available for the selected location.");
              handleManualLocationSubmit(true);
            }
          });
        } catch (error) {
          console.error("Error initializing Google Autocomplete:", error);
          toast.error(
            "Failed to initialize location suggestions. Please try manual entry."
          );
        }
      }
    }

    return () => {
      if (autocompleteInstance.current) {
        if (window.google && window.google.maps && window.google.maps.event) {
          window.google.maps.event.clearInstanceListeners(
            autocompleteInstance.current
          );
        }
        autocompleteInstance.current = null;
      }
    };
  }, [isGoogleMapsLoaded, showLocationModal]);

  useEffect(() => {
    const storedLocation = localStorage.getItem("userLocation");

    if (storedLocation) {
      setCurrentLocation(JSON.parse(storedLocation));
    } else if (navigator.geolocation && isGoogleMapsLoaded) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const locationDetails = await getAddressFromCoordinates(
            latitude,
            longitude
          );

          if (locationDetails) {
            setCurrentLocation(locationDetails);
            localStorage.setItem(
              "userLocation",
              JSON.stringify(locationDetails)
            );
          }
        },
        (error) => {
           toast.error("Failed to fetch your location.");
        }
      );
    }
  }, [isGoogleMapsLoaded]);

  useEffect(() => {
    document.body.style.overflow = showLocationModal ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showLocationModal]);

  const handleLocationClick = () => {
    setShowLocationModal(true);
    setManualLocationInput("");
  };

  const handleCloseModal = () => {
    setShowLocationModal(false);
  };

  const getAddressFromCoordinates = async (latitude, longitude) => {
    if (!isGoogleMapsLoaded || !window.google || !window.google.maps) {
      toast.error("Google Maps API not fully loaded. Please try again.");
      return null;
    }

    const geocoder = new window.google.maps.Geocoder();
    const latlng = { lat: latitude, lng: longitude };

    try {
      const { results } = await geocoder.geocode({ location: latlng });

      if (results && results.length > 0) {
        let city = "Unknown City";
        let state = "Unknown Region";
        let country = "Unknown Country";
        let area = "";

        const processComponents = (components) => {
          components.forEach((component) => {
            if (component.types.includes("locality")) {
              city = component.long_name;
            } else if (
              component.types.includes("administrative_area_level_1")
            ) {
              state = component.long_name;
            } else if (component.types.includes("country")) {
              country = component.long_name;
            }

            if (!area) {
              const areaTypes = [
                "neighborhood",
                "sublocality_level_1",
                "sublocality",
                "political",
                "route",
              ];

              for (const type of areaTypes) {
                if (component.types.includes(type)) {
                  area = component.long_name;
                  break;
                }
              }
            }
          });
        };

        processComponents(results[0].address_components);

        // If area is still empty, check other results
        if (!area) {
          for (let i = 1; i < results.length && !area; i++) {
            processComponents(results[i].address_components);
          }
        }

        // Final fallback for area
        if (!area && results[0].formatted_address) {
          area = results[0].formatted_address.split(",")[0].trim();
        }

        const displayState = state !== "Unknown Region" ? state : country;

        return {
          city,
          state: displayState,
          latitude,
          longitude,
          country,
          area,
        };
      } else {
        toast.warn("Could not find address details for your current location.");
        return {
          city: "Location Found",
          state: `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`,
          latitude,
          longitude,
          country: "",
          area: "",
        };
      }
    } catch (error) {
      console.error("Error reverse geocoding with Google Maps:", error);
      toast.error(
        "Failed to fetch address details. Check console for more info."
      );
      return null;
    }
  };

  const handleUseCurrentLocation = async () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser.");
      return;
    }
    if (!isGoogleMapsLoaded) {
      toast.info("Loading location services... please try again shortly.");
      return;
    }

    toast.info("Fetching your current location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const locationDetails = await getAddressFromCoordinates(
          latitude,
          longitude
        );

        if (locationDetails) {
          setCurrentLocation(locationDetails);
          localStorage.setItem("userLocation", JSON.stringify(locationDetails));
          toast.success("Location updated successfully!");
        } else {
          const fallbackLocation = {
            city: "Unknown Address",
            state: `Lat: ${latitude.toFixed(4)}, Lon: ${longitude.toFixed(4)}`,
            latitude,
            longitude,
            country: "",
            area: "",
          };
          setCurrentLocation(fallbackLocation);
          localStorage.setItem(
            "userLocation",
            JSON.stringify(fallbackLocation)
          );
        }
        setShowLocationModal(false);
      },
      (error) => {
        let errorMessage = "Unable to retrieve your location.";
        if (error.code === error.PERMISSION_DENIED) {
          errorMessage =
            "Location access denied. Please allow location services in your browser settings.";
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          errorMessage = "Location information is unavailable.";
        } else if (error.code === error.TIMEOUT) {
          errorMessage = "The request to get location timed out.";
        }
        console.error("Geolocation error:", error);
        toast.error(errorMessage);
      }
    );
  };

  const handleManualLocationSubmit = async (
    fromAutocompleteFallback = false
  ) => {
    if (manualLocationInput.trim() === "") {
      if (!fromAutocompleteFallback) {
        toast.info("Please enter a location.");
      }
      return;
    }

    if (!isGoogleMapsLoaded) {
      toast.info("Location services are not loaded. Please try again.");
      return;
    }

    toast.info("Fetching details for manual entry...");

    const geocoder = new window.google.maps.Geocoder();
    try {
      const { results } = await geocoder.geocode({
        address: manualLocationInput,
      });

      if (results && results.length > 0) {
        const place = results[0];
        let city = "Unknown City";
        let state = "";
        let latitude = place.geometry.location.lat();
        let longitude = place.geometry.location.lng();
        let country = "";
        let area = "";

        const addressComponents = place.address_components;
        addressComponents.forEach((component) => {
          if (component.types.includes("locality")) {
            city = component.long_name;
          } else if (component.types.includes("administrative_area_level_1")) {
            state = component.long_name;
          } else if (component.types.includes("country")) {
            country = component.long_name;
          }

          if (!area) {
            const areaTypes = [
              "neighborhood",
              "sublocality_level_1",
              "sublocality",
              "political",
              "route",
            ];

            for (const type of areaTypes) {
              if (component.types.includes(type)) {
                area = component.long_name;
                break;
              }
            }
          }
        });

        if (!area && place.formatted_address) {
          area = place.formatted_address.split(",")[0].trim();
        }

        const newLocation = {
          city,
          state,
          latitude,
          longitude,
          country,
          area,
        };
        setCurrentLocation(newLocation);
        localStorage.setItem("userLocation", JSON.stringify(newLocation));
        setShowLocationModal(false);
        toast.success("Manual location set successfully!");
      } else {
        const basicLocation = {
          city: manualLocationInput.trim(),
          state: "",
          latitude: null,
          longitude: null,
          country: "",
          area: manualLocationInput.trim(), // Use input as area
        };
        setCurrentLocation(basicLocation);
        localStorage.setItem("userLocation", JSON.stringify(basicLocation));
        setShowLocationModal(false);
        toast.warn(
          "Could not find detailed information for the entered location. Only basic details saved."
        );
      }
    } catch (error) {
      console.error("Error geocoding manual input:", error);
      toast.error("Failed to process manual location. Please try again.");
      const basicLocation = {
        city: manualLocationInput.trim(),
        state: "",
        latitude: null,
        longitude: null,
        country: "",
        area: manualLocationInput.trim(),
      };
      setCurrentLocation(basicLocation);
      localStorage.setItem("userLocation", JSON.stringify(basicLocation));
      setShowLocationModal(false);
    }
  };

  return (
    <header className="bg-white shadow-sm py-4 px-6 relative z-50">
      {/* Mobile Header */}
      <div className="block md:hidden">
        <PublicMobileHeader
          currentLocation={currentLocation}
          onLocationClick={handleLocationClick}
        />
      </div>

      {/* Desktop Header */}
      <div className="hidden md:block">
        <div className="max-w-screen-xl mx-auto flex justify-between items-center">
          {/* Logo and Location */}
          <div className="flex items-center space-x-6">
            <img
              src="/upswap.png"
              alt="UpSway Logo"
              className="h-10 cursor-pointer"
              onClick={() => navigate("/home")}
            />

            <div
              className="flex items-center cursor-pointer"
              onClick={handleLocationClick}
            >
              <FaMapMarkerAlt className="text-gray-500 text-base flex-shrink-0" />
              <div className="flex flex-col ml-1">
                <div className="font-semibold leading-none">
                  {currentLocation.city}
                </div>
                <div className="text-xs text-gray-500 leading-none">
                  {currentLocation.area && `${currentLocation.area}, `}
                  {currentLocation.state}
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Links */}
          <nav className="hidden md:flex items-center space-x-6 text-sm font-medium text-gray-700 gap-4">
            <button
              onClick={() => navigate("/LocalActivities")}
              className="hover:text-orange-500 transition"
            >
              Browse Activities
            </button>
            <button
              onClick={() => navigate("/PopularDeals")}
              className="hover:text-orange-500 transition"
            >
              Explore Deals
            </button>
            <button
              onClick={() => navigate("/vendor")}
              className="hover:text-orange-500 transition"
            >
              Find Vendors
            </button>
          </nav>

          {/* Auth Buttons */}
          <div className="flex space-x-4">
            <button
              onClick={() => navigate("/createAccount")}
              className="bg-orange-500 text-white font-semibold px-5 py-2 rounded-md hover:bg-orange-600 transition"
            >
              Sign Up
            </button>
            <button
              onClick={() => navigate("/login")}
              className="border border-gray-300 text-gray-700 font-semibold px-5 py-2 rounded-md hover:bg-gray-100 transition"
            >
              Login
            </button>
          </div>
        </div>

        {/* Location Modal */}
        {showLocationModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
            <div className="relative bg-white p-6 rounded-lg shadow-xl w-full max-w-sm mx-4 md:max-w-md md:p-8">
              <button
                onClick={handleCloseModal}
                className="absolute top-3 right-3 p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                aria-label="Close location selection"
              >
                <FaTimes size={20} />
              </button>

              <h2 className="text-xl font-bold mb-4 text-gray-800">
                Select Your Location
              </h2>

              <button
                onClick={handleUseCurrentLocation}
                disabled={!isGoogleMapsLoaded}
                className={`w-full py-2 px-4 rounded-md transition mb-4 ${
                  !isGoogleMapsLoaded
                    ? "bg-blue-300 cursor-not-allowed"
                    : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
              >
                <FaMapMarkerAlt className="inline-block mr-2" />
                {isGoogleMapsLoaded
                  ? "Use My Current Location"
                  : "Loading Location Services..."}
              </button>

              <div className="text-center text-gray-500 mb-4 font-semibold">
                - OR -
              </div>

              <input
                type="text"
                ref={locationInputModalRef}
                placeholder="Enter location manually (e.g., New York, USA)"
                className="w-full border border-gray-300 p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                value={manualLocationInput}
                onChange={(e) => setManualLocationInput(e.target.value)}
              />
              <button
                onClick={handleManualLocationSubmit}
                className="w-full bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600 transition mb-4"
              >
                Set Location
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default PublicHeader;
